/**
 * editRows (編集バッファ) から UI が必要とする純派生を導出する hook。
 *
 * useForecastDraftEditor から async RPC cluster (保存直列化 saveRunRef /
 * save・discard) と独立な「純計算クラスタ」を切り出した seam。責務はサーバー
 * 状態でなく編集バッファの導出のみ: dirty 集合 (currentDirtyKomas /
 * bgTotalDirtyKomas / anyDirty)・BG全体ライブ合計 (bgTotalLiveSlots)・現在の
 * 表示対象 (currentTarget) とその表示スロット/ラベルを算出する。dirty 判定の
 * 意味論は `models/forecastDirtyState` (= computeForecastDiff) が SoT で、本 hook
 * はその memoization と選択対象 (currentTarget) 解決に徹する (async に依存しない)。
 */
import { useMemo } from 'react'

import type { SlotValue } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'

import {
  anyRowDirty,
  isRowDirty,
  rowDirtyKomas,
  unionDirtyKomas,
} from '~/pages/DemandForecastDaily/models/forecastDirtyState'
import {
  bgTotalBreakdownSlots,
  calcBgTotalSlots,
  protoSlotsToKoma,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  KomaSlots,
  RetailerEditRow,
  TargetScope,
  UnattributedEditRow,
} from '~/pages/DemandForecastDaily/models/types'

type CurrentTarget =
  | { kind: 'bgTotal' }
  | { kind: 'unattributed' }
  | { kind: 'retailer'; row: RetailerEditRow }

type UseForecastDirtyStateParams = {
  editRows: Record<string, RetailerEditRow>
  /** page.bgTotalSlots (Σ小売の read-only 合計)。page 未ロード時は undefined。 */
  bgTotalSlots: SlotValue[] | undefined
  unattributedRow?: UnattributedEditRow
  /** Read-only callers may supply slots without an editor baseline. */
  unattributedSlots?: KomaSlots | SlotValue[]
  targetScope: TargetScope
  selectedRetailerBusinessCd: string | undefined
}

export type UseForecastDirtyStateResult = {
  editRowList: RetailerEditRow[]
  bgTotalLiveSlots: KomaSlots
  currentTarget: CurrentTarget
  displaySlots: KomaSlots
  displayLabel: string
  currentEditRow: RetailerEditRow | undefined
  currentDirtyKomas: Set<number>
  /**
   * 選択中小売の編集行が dirty か (値差 or 取込元メタ差、= `isRowDirty` の full
   * 判定)。currentDirtyKomas.size > 0 では取込元メタのみの差 (koma 差なし) を
   * 取りこぼすため別に持つ。変更破棄 (canDiscard) の唯一の判定源。BG_TOTAL
   * (複数小売) ビューは currentEditRow が undefined になるため false。
   */
  currentRowDirty: boolean
  bgTotalDirtyKomas: Set<number>
  anyDirty: boolean
}

export function useForecastDirtyState({
  editRows,
  bgTotalSlots,
  unattributedRow,
  unattributedSlots,
  targetScope,
  selectedRetailerBusinessCd,
}: UseForecastDirtyStateParams): UseForecastDirtyStateResult {
  const { t } = useTranslation()

  const normalizedUnattributedSlots = useMemo<KomaSlots | undefined>(() => {
    if (unattributedRow !== undefined) return unattributedRow.forecastSlots
    if (unattributedSlots === undefined) return undefined
    const first = unattributedSlots[0]
    return typeof first === 'number' || first === undefined
      ? (unattributedSlots as KomaSlots)
      : protoSlotsToKoma(unattributedSlots as SlotValue[])
  }, [unattributedRow, unattributedSlots])

  const editRowList = useMemo(() => Object.values(editRows), [editRows])

  const bgTotalLiveSlots = useMemo<KomaSlots>(() => {
    const retailerSlots = editRowList.map((r) => r.forecastSlots)
    const breakdownSlots = bgTotalBreakdownSlots(
      retailerSlots,
      normalizedUnattributedSlots === undefined
        ? undefined
        : normalizedUnattributedSlots,
    )
    if (retailerSlots.length === 0 && bgTotalSlots !== undefined) {
      return protoSlotsToKoma(bgTotalSlots)
    }
    return calcBgTotalSlots(breakdownSlots)
  }, [editRowList, bgTotalSlots, normalizedUnattributedSlots])

  const currentTarget = useMemo<CurrentTarget>(() => {
    if (targetScope === 'UNATTRIBUTED') return { kind: 'unattributed' }
    if (
      targetScope === 'RETAILER' &&
      selectedRetailerBusinessCd !== undefined
    ) {
      const row = editRows[selectedRetailerBusinessCd]
      if (row !== undefined) return { kind: 'retailer', row }
    }
    return { kind: 'bgTotal' }
  }, [targetScope, selectedRetailerBusinessCd, editRows])

  const displaySlots: KomaSlots =
    currentTarget.kind === 'retailer'
      ? currentTarget.row.forecastSlots
      : currentTarget.kind === 'unattributed'
        ? (normalizedUnattributedSlots ?? []).map((value) => value ?? 0)
        : bgTotalLiveSlots

  const displayLabel =
    currentTarget.kind === 'retailer'
      ? currentTarget.row.displayName
      : currentTarget.kind === 'unattributed'
        ? t('page:demandForecastDaily.toolbar.unattributed')
        : t('page:demandForecastDaily.toolbar.bgTotal')

  const currentEditRow =
    currentTarget.kind === 'retailer' ? currentTarget.row : undefined

  const currentDirtyKomas = useMemo<Set<number>>(
    () =>
      currentEditRow === undefined ? new Set() : rowDirtyKomas(currentEditRow),
    [currentEditRow],
  )

  const currentRowDirty = useMemo<boolean>(
    () => (currentEditRow === undefined ? false : isRowDirty(currentEditRow)),
    [currentEditRow],
  )

  const bgTotalDirtyKomas = useMemo<Set<number>>(
    () =>
      unionDirtyKomas(
        unattributedRow === undefined
          ? editRowList
          : [...editRowList, unattributedRow],
      ),
    [editRowList, unattributedRow],
  )

  const anyDirty = useMemo(
    () =>
      anyRowDirty(editRowList) ||
      (unattributedRow !== undefined && isRowDirty(unattributedRow)),
    [editRowList, unattributedRow],
  )

  return {
    editRowList,
    bgTotalLiveSlots,
    currentTarget,
    displaySlots,
    displayLabel,
    currentEditRow,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
  }
}
